import { Box, Button, IconButton } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import DropZoneComponent from 'components/DropZoneComponent';
import { useWhapi } from 'context';
import { Baseline, Image, Loader2 } from 'lucide-react';
import React from 'react';
import { sentMessageResultSchema } from 'type/type';
import { z } from 'zod';
import { submitImageByTag, submitImageByTagAdvance, submitImageByTagExclude, submitMessageByTag, submitMessageByTagAdvance, submitMessageByTagExclude } from '../services/queryServices';

export interface ImageFile {
  name: string;
  lastModified: number;
  lastModifiedDate: Date;
  size: number;
  type: string;
  base64: string;
}
export type DroppedImage = ImageFile | null;
const InputBox = () => {
  const { selectedTags, excludedGroups, advanceGroups, preprocessOutput } = useWhapi();
  const [input, setInput] = React.useState('');
  const [image, setImage] = React.useState<DroppedImage>(null);
  const [isUploadImage, setIsUploadImage] = React.useState(false);
  const onSuccess = (result: any) => {
    const resultArr: z.infer<typeof sentMessageResultSchema>[] = [];
    result.data.forEach((item: any) => {
      if (item !== null && item.status === 200) {
        resultArr.push(item.data);
      }
    });
    preprocessOutput(resultArr);
    setInput('');
    setImage(null);
  };
  const onError = (error: any) => console.log(error);

  const sendMessageWithExclude = useMutation({
    mutationFn: (data: { excluded: string[]; tag: string }) =>
      submitMessageByTagExclude({
        tag: data.tag,
        message: input,
        excluded: data.excluded,
      }),
    onSuccess,
    onError,
  });

  const sendMessageWithAdvance = useMutation({
    mutationFn: (data: { advance: string[]; tag: string }) =>
      submitMessageByTagAdvance({
        tag: data.tag,
        message: input,
        advance: data.advance,
      }),
    onSuccess,
    onError,
  });

  const sendMessage = useMutation({
    mutationFn: (tag: string) =>
      submitMessageByTag({
        tag: tag,
        message: input,
      }),
    onSuccess,
    onError,
  });

  const sendImageWithExclude = useMutation({
    mutationFn: (data: { excluded: string[]; tag: string }) =>
      submitImageByTagExclude({
        tag: data.tag,
        image: image?.base64,
        excluded: data.excluded,
      }),
    onSuccess,
    onError,
  });

  const sendImageWithAdvance = useMutation({
    mutationFn: (data: { advance: string[]; tag: string }) =>
      submitImageByTagAdvance({
        tag: data.tag,
        image: image?.base64,
        advance: data.advance,
      }),
    onSuccess,
    onError,
  });

  const sendImage = useMutation({
    mutationFn: (tag: string) =>
      submitImageByTag({
        tag: tag,
        image: image?.base64,
      }),
    onSuccess,
    onError,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      postMessage();
    }
  };

  const isPending = sendMessageWithExclude.isPending || sendMessageWithAdvance.isPending || sendMessage.isPending || sendImageWithExclude.isPending || sendImageWithAdvance.isPending || sendImage.isPending;

  const postMessage = () => {
    selectedTags.map(tag => {
      const advance = advanceGroups.find(group => group.name === tag)?.members.map(member => member.RowKey) || [];
      const excluded = excludedGroups.find(group => group.name === tag)?.members.map(member => member.RowKey) || [];
      if (!isUploadImage) {
        if (advance && advance.length > 0) {
          return sendMessageWithAdvance.mutate({ advance, tag });
        }
        if (excluded && excluded.length > 0) {
          return sendMessageWithExclude.mutate({ excluded, tag });
        }
        return sendMessage.mutate(tag);
      } else {
        if (advance && advance.length > 0) {
          return sendImageWithAdvance.mutate({ advance, tag });
        }
        if (excluded && excluded.length > 0) {
          return sendImageWithExclude.mutate({ excluded, tag });
        }
        return sendImage.mutate(tag);
      }
    });
  };

  return (
    <>
      <Box
        sx={{
          minHeight: 100,
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <IconButton onClick={() => setIsUploadImage(false)}>
            <Baseline color={!isUploadImage ? 'var(--primary)' : undefined} size={20} />
          </IconButton>
          <IconButton onClick={() => setIsUploadImage(true)}>
            <Image color={isUploadImage ? 'var(--primary)' : undefined} size={20} />
          </IconButton>
        </Box>
        {!isUploadImage ? (
          <textarea value={input} placeholder={'Type message here...'} onKeyDown={handleKeyDown} onChange={e => setInput(e.target.value)} style={{ flexGrow: 1, borderRadius: 5, padding: '20px' }} autoFocus />
        ) : (
          <DropZoneComponent image={image} setImage={setImage} />
        )}
      </Box>
      <Button variant='contained' sx={{ ml: 2, width: '120px', height: '100%' }} onClick={postMessage}>
        {isPending && <Loader2 className={'animated-rotation'} />} Post
      </Button>
    </>
  );
};

export default InputBox;
